import { Add, Close, Visibility } from '@mui/icons-material'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { EPhysiciansTitle, ImplantReportType } from 'common/types'
import CustomDatePicker from 'components/molecules/DatePicker/DatePicker'
import { CustomModal } from 'components/molecules/Modal/Modal'
import dayjs from 'lib/dayjs'
import '../ImplantReportSender.scss'
import { Delete } from '@mui/icons-material'
import TextInput from 'components/molecules/TextInput/TextInput'
import AlertDialog from 'components/AlertDialog/AlertDialog'
import AutocompleteInput from '../../AutocompleteInput/AutocompleteInput'
import { useImplantReportSenderModalLogic } from './ImplantReportSenderModal.logic'
import { ImplantReportSenderModalProps } from '../ImplantReportSender.types'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { styles } from './ImplantReportSenderModal.styles'
import Box from '@mui/material/Box'
import { ImplantReportPreviewModal } from '../ImplantReportPreviewModal/ImplantReportPreviewModal'
import { LoadingButton } from '@mui/lab'

export const ImplantReportSenderModal = ({
  assetIds,
  inputModalOpen,
  formatEnumValue,
  surgeryDate,
  surgeryId,
  implantReportStatus,
  implantReportInputs,
  setImplantReportInputs,
  unformatEnumValue,
  setErrorMessage,
  setInputModalOpen,
}: ImplantReportSenderModalProps) => {
  const {
    attachments,
    fileInputRef,
    physiciansTitle,
    validationErrors,
    extractionValues,
    reportAlreadySentModal,
    sendImplantReportMutation,
    previewImplantReportLoading,
    previewModalOpen,
    previewPdfUrl,
    handleAttach,
    submitReport,
    handleOnClose,
    validateAndSubmit,
    validateAndPreview,
    handleClosePreview,
    handleChangeFormData,
    handleRemoveAttachment,
    handleAddExtractionInput,
    setReportAlreadySentModal,
    handleChangeExtractionInput,
    handleChangeImplantNumber,
    handleRemoveExtractionInput,
    handleRemoveImplantNumber,
    handleAttachementFileChange,
    implantNumbers,
    handleAddImplantNumber,
    isCommaSeparatedInput,
    isBch,
  } = useImplantReportSenderModalLogic({
    assetIds,
    surgeryId,
    implantReportStatus,
    implantReportInputs,
    setImplantReportInputs,
    unformatEnumValue,
    setErrorMessage,
    setInputModalOpen,
    formatEnumValue,
  })

  const autoFocus = {
    extraction: false,
    firstStage: false,
    singleStage: false,
    other: false,
  }

  if (
    implantReportInputs['reportType'] === 'EXTRACTION' ||
    implantReportInputs['reportType'] === 'EXTRACTION_AND_BONE_GRAFT'
  ) {
    autoFocus.extraction = true
  } else if (implantReportInputs['reportType'] === 'FIRST_STAGE') {
    autoFocus.firstStage = true
  } else if (implantReportInputs['reportType'] === 'SINGLE_STAGE') {
    autoFocus.singleStage = true
  } else if (implantReportInputs['reportType'] === 'OTHER') {
    autoFocus.other = true
  }

  return (
    <>
      <CustomModal
        open={inputModalOpen}
        secondaryButtonAction={handleOnClose}
        header={`${formatEnumValue(
          implantReportInputs?.['reportType'] as keyof typeof ImplantReportType
        )} Report`}
        isLoadingState={sendImplantReportMutation.loading}
        primaryButtonText="Submit"
        handleOnSubmit={validateAndSubmit}
        icon={<></>}
      >
        {(implantReportInputs['reportType'] === 'EXTRACTION' ||
          implantReportInputs['reportType'] ===
            'EXTRACTION_AND_BONE_GRAFT') && (
          <div>
            {isCommaSeparatedInput ? (
              <TextInput
                variant="outlined"
                placeholder="Extraction sites (comma-separated)*"
                label="Extraction Sites*"
                type="text"
                fullwidth={true}
                value={extractionValues[0] || ''}
                handleChangeFormData={(e) => {
                  const value = e.target.value
                  handleChangeExtractionInput(0, value)
                }}
                errorMessage={validationErrors?.extractionValues?.[0]}
                autoFocus={true}
                focused={true}
                inputProps={{
                  inputMode: 'text',
                  pattern: '.*',
                }}
              />
            ) : (
              <div className="extraction-report-input-with-button">
                <div className="extraction-report-input-array">
                  {extractionValues.map((value, index) => (
                    <TextInput
                      key={index}
                      variant="outlined"
                      placeholder="Extraction site*"
                      label="Extraction Site*"
                      type="text"
                      fullwidth={false}
                      value={value}
                      handleChangeFormData={(e) => {
                        handleChangeExtractionInput(index, e.target.value)
                      }}
                      InputProps={{
                        endAdornment:
                          extractionValues.length > 1 ? (
                            <IconButton
                              onClick={() => handleRemoveExtractionInput(index)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          ) : null,
                      }}
                      autoFocus={true}
                      focused={true}
                      errorMessage={validationErrors?.extractionValues?.[index]}
                      inputProps={{
                        inputMode: 'text',
                        pattern: '.*',
                      }}
                    />
                  ))}
                </div>
                <IconButton
                  size="small"
                  onClick={handleAddExtractionInput}
                  disableFocusRipple
                  disableTouchRipple
                >
                  <Add className="add-icon" />
                </IconButton>
              </div>
            )}
            <TextInput
              variant="outlined"
              placeholder="Additional Comments"
              label="Additional Comments"
              handleChangeFormData={(e) => {
                handleChangeFormData('additionalComments', e.target.value)
              }}
              value={implantReportInputs.reportInputs['additionalComments']}
            />
          </div>
        )}

        {(implantReportInputs['reportType'] === 'FIRST_STAGE' ||
          implantReportInputs['reportType'] === 'SINGLE_STAGE') && (
          <div className="first-single-stage">
            <CustomDatePicker
              sx={{ width: '80%' }}
              label="Next Appointment Date"
              className="next-date-picker"
              value={
                implantReportInputs.reportInputs['nextApptDate'] as dayjs.Dayjs
              }
              onDateSelection={(date) => {
                handleChangeFormData('nextApptDate', date)
              }}
              minDate={dayjs(surgeryDate).add(1, 'day')}
              errorMessage={validationErrors?.nextApptDate?.[0]}
            />

            <TextInput
              variant="outlined"
              className="next-appt-input"
              placeholder={`${
                implantReportInputs['reportType'] === 'FIRST_STAGE'
                  ? '2nd Stage'
                  : 'Final check'
              } will be scheduled in ____ months*`}
              label={`${
                implantReportInputs['reportType'] === 'FIRST_STAGE'
                  ? '2nd Stage'
                  : 'Final check'
              } will be scheduled in ____ months*`}
              handleChangeFormData={(e) => {
                handleChangeFormData('nextApptInMonths', Number(e.target.value))
              }}
              value={implantReportInputs.reportInputs['nextApptInMonths']}
              type="number"
              errorMessage={validationErrors?.nextApptInMonths?.[0]}
              autoFocus={autoFocus.firstStage || autoFocus.singleStage}
              focused={autoFocus.firstStage || autoFocus.singleStage}
            />

            {implantReportInputs['reportType'] === 'SINGLE_STAGE' && (
              <TextInput
                variant="outlined"
                placeholder="Additional Comments"
                label="Additional Comments"
                handleChangeFormData={(e) => {
                  handleChangeFormData('additionalComments', e.target.value)
                }}
                value={implantReportInputs.reportInputs['additionalComments']}
              />
            )}

            {implantReportInputs['reportType'] === 'FIRST_STAGE' && (
              <div className="first-single-stage">
                <AutocompleteInput
                  id={'abutment-type-menu'}
                  handleChangeFormData={(e) => {
                    handleChangeFormData(
                      'secondStageAbutmentType',
                      e.target.value as string
                    )
                  }}
                  name={'abutment-type'}
                  label="Select Second Stage Abutment Type*"
                  variant="outlined"
                  value={
                    implantReportInputs.reportInputs['secondStageAbutmentType']
                  }
                  options={['Healing', 'Cementable', 'Other']}
                  autoSelect={false}
                  disableClearable
                  errorMessage={validationErrors?.secondStageAbutmentType?.[0]}
                />
                {implantReportInputs.reportInputs['secondStageAbutmentType'] ===
                  'Other' && (
                  <TextInput
                    variant="outlined"
                    className="other-abutment-input"
                    placeholder="Other Abutment Type"
                    label="Other Abutment Type"
                    handleChangeFormData={(e) => {
                      handleChangeFormData('otherAbutmentType', e.target.value)
                    }}
                    value={
                      implantReportInputs.reportInputs['otherAbutmentType']
                    }
                  />
                )}
                <TextInput
                  variant="outlined"
                  placeholder="Additional Comments"
                  label="Additional Comments"
                  handleChangeFormData={(e) => {
                    handleChangeFormData('additionalComments', e.target.value)
                  }}
                  value={implantReportInputs.reportInputs['additionalComments']}
                />
              </div>
            )}
          </div>
        )}

        {(implantReportInputs['reportType'] === 'SECOND_STAGE' ||
          implantReportInputs['reportType'] === 'FINAL_CHECK') && (
          <div className="restorative-report-input-array">
            <TextInput
              variant="outlined"
              placeholder="Additional Comments"
              label="Additional Comments"
              handleChangeFormData={(e) => {
                handleChangeFormData('additionalComments', e.target.value)
              }}
              value={implantReportInputs.reportInputs['additionalComments']}
            />
            <CustomDatePicker
              label="Restorative Phase Date"
              className="restorative-date-picker"
              value={
                implantReportInputs.reportInputs[
                  'restorativePhaseDate'
                ] as dayjs.Dayjs
              }
              onDateSelection={(date) => {
                handleChangeFormData('restorativePhaseDate', date)
              }}
              minDate={dayjs(surgeryDate)}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              className="secondary-text"
            >
              If a restorative phase date is not entered, then the report will
              default to "The patient understands that they should contact your
              office to begin the restorative phase within 1-2 weeks."
            </Typography>
          </div>
        )}

        {implantReportInputs['reportType'] === 'OTHER' && (
          <TextInput
            id="other-implant-report-memo"
            label={
              physiciansTitle === EPhysiciansTitle.ReferringPhysicians
                ? 'Please add additional comments such as type of consultation here'
                : 'Please add any additional comments and any other report information here'
            }
            multiline
            rows={4}
            variant="outlined"
            handleChangeFormData={(e) => {
              handleChangeFormData('additionalComments', e.target.value)
            }}
            value={implantReportInputs.reportInputs['additionalComments']}
            errorMessage={validationErrors?.additionalComments?.[0]}
            autoFocus={autoFocus.other}
            focused={autoFocus.other}
          />
        )}

        {implantReportInputs['reportType'] === 'IMPLANT' && (
          <div className="implant-report">
            <CustomDatePicker
              sx={{ width: '80%', marginTop: '10px' }}
              label={isBch ? 'Next Appointment Date' : 'Torque Test Date*'}
              className="torque-test-date-picker"
              value={
                implantReportInputs.reportInputs[
                  isBch ? 'nextApptDate' : 'torqueTestDate'
                ] as dayjs.Dayjs
              }
              onDateSelection={(date) => {
                handleChangeFormData(
                  isBch ? 'nextApptDate' : 'torqueTestDate',
                  date
                )
              }}
              minDate={dayjs(surgeryDate)}
              errorMessage={
                isBch
                  ? validationErrors?.nextApptDate?.[0]
                  : validationErrors?.torqueTestDate?.[0]
              }
            />

            <TextInput
              variant="outlined"
              placeholder="Additional Comments"
              label="Additional Comments"
              handleChangeFormData={(e) => {
                handleChangeFormData('additionalComments', e.target.value)
              }}
              value={implantReportInputs.reportInputs['additionalComments']}
              sx={{ marginTop: '20px' }}
            />
          </div>
        )}

        {implantReportInputs['reportType'] === 'TORQUE_TEST' && (
          <Box
            className="torque-test-report"
            sx={styles['.torque-test-report']}
          >
            {isCommaSeparatedInput ? (
              <TextInput
                variant="outlined"
                placeholder="Implant numbers (comma-separated)*"
                label="Implant Numbers*"
                type="text"
                fullwidth={true}
                value={implantNumbers.join(',')}
                handleChangeFormData={(e) => {
                  const value = e.target.value
                  handleChangeImplantNumber(0, value)
                }}
                errorMessage={validationErrors?.implantNumbers?.[0]}
                autoFocus={true}
                focused={true}
                inputProps={{
                  inputMode: 'text',
                  pattern: '.*',
                }}
              />
            ) : (
              <div className="extraction-report-input-with-button">
                <div className="extraction-report-input-array">
                  {implantNumbers.map((value, index) => (
                    <TextInput
                      key={index}
                      variant="outlined"
                      placeholder="Implant Number*"
                      label="Implant Number*"
                      type="text"
                      fullwidth={false}
                      value={value}
                      handleChangeFormData={(e) =>
                        handleChangeImplantNumber(index, e.target.value)
                      }
                      InputProps={{
                        endAdornment:
                          implantNumbers.length > 1 ? (
                            <IconButton
                              onClick={() => handleRemoveImplantNumber(index)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          ) : null,
                      }}
                      errorMessage={validationErrors?.implantNumbers?.[index]}
                      inputProps={{
                        inputMode: 'text',
                        pattern: '.*',
                      }}
                    />
                  ))}
                </div>
                <IconButton
                  size="small"
                  onClick={handleAddImplantNumber}
                  disableFocusRipple
                  disableTouchRipple
                >
                  <Add className="add-icon" />
                </IconButton>
              </div>
            )}
            <div className="implant-parts-options">
              <AutocompleteInput
                variant="outlined"
                placeholder="Implant Parts Options*"
                label="Implant Parts Options*"
                value={implantReportInputs.reportInputs.implantPartsOption}
                handleChangeFormData={(e) =>
                  handleChangeFormData(
                    'implantPartsOption',
                    e.target.value as string
                  )
                }
                options={[
                  'Implants Handed to Patient',
                  'Implant Parts Mailed to Referral',
                ]}
                errorMessage={validationErrors?.implantPartsOption?.[0]}
                autoSelect={false}
                disableClearable
              />
            </div>
          </Box>
        )}

        {implantReportInputs['reportType'] === 'IMPLANT_AND_EXTRACTION' && (
          <Box
            className="implant-and-extraction-report"
            sx={styles['.torque-test-report']}
          >
            {isCommaSeparatedInput ? (
              <TextInput
                variant="outlined"
                placeholder="Extraction sites (comma-separated)*"
                label="Extraction Sites*"
                type="text"
                fullwidth={true}
                value={extractionValues[0] || ''}
                handleChangeFormData={(e) => {
                  const value = e.target.value
                  handleChangeExtractionInput(0, value)
                }}
                errorMessage={validationErrors?.extractionValues?.[0]}
                autoFocus={true}
                focused={true}
                inputProps={{
                  inputMode: 'text',
                  pattern: '.*',
                }}
              />
            ) : (
              <Box
                className="extraction-report-input-with-button"
                sx={styles['.implant-number-input-with-button']}
              >
                <Box
                  className="extraction-report-input-array"
                  sx={styles['.implant-number-input-array']}
                >
                  {extractionValues.map((value, index) => (
                    <TextInput
                      key={index}
                      variant="outlined"
                      placeholder="Extraction site*"
                      label="Extraction Site*"
                      type="text"
                      fullwidth={false}
                      value={value}
                      handleChangeFormData={(e) => {
                        handleChangeExtractionInput(index, e.target.value)
                      }}
                      InputProps={{
                        endAdornment:
                          extractionValues.length > 1 ? (
                            <IconButton
                              onClick={() => handleRemoveExtractionInput(index)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          ) : null,
                      }}
                      autoFocus={true}
                      focused={true}
                      errorMessage={validationErrors?.extractionValues?.[index]}
                      inputProps={{
                        inputMode: 'text',
                        pattern: '.*',
                      }}
                    />
                  ))}
                </Box>
                <IconButton
                  size="small"
                  onClick={handleAddExtractionInput}
                  disableFocusRipple
                  disableTouchRipple
                  sx={styles['.add-icon']}
                >
                  <Add className="add-icon" />
                </IconButton>
              </Box>
            )}
            <Box sx={{ width: '100%' }}>
              <CustomDatePicker
                sx={{ width: '100%', marginTop: '20px' }}
                label="Torque Test Date*"
                className="torque-test-date-picker"
                value={
                  implantReportInputs.reportInputs[
                    'torqueTestDate'
                  ] as dayjs.Dayjs
                }
                onDateSelection={(date) => {
                  handleChangeFormData('torqueTestDate', date)
                }}
                minDate={dayjs(surgeryDate)}
                errorMessage={validationErrors?.torqueTestDate?.[0]}
              />
            </Box>
            <TextInput
              variant="outlined"
              placeholder="Additional Comments"
              label="Additional Comments"
              handleChangeFormData={(e) => {
                handleChangeFormData('additionalComments', e.target.value)
              }}
              value={implantReportInputs.reportInputs['additionalComments']}
              sx={{ marginTop: '20px' }}
            />
          </Box>
        )}

        {implantReportInputs['reportType'] === 'CONSULTATION' && (
          <Box
            className="consultation-report"
            sx={styles['.torque-test-report']}
          >
            <TextInput
              variant="outlined"
              placeholder="Consultation Reason*"
              label="Consultation Reason*"
              multiline
              rows={4}
              handleChangeFormData={(e) => {
                handleChangeFormData('consultationReason', e.target.value)
              }}
              value={implantReportInputs.reportInputs['consultationReason']}
              errorMessage={validationErrors?.consultationReason?.[0]}
            />
            <TextInput
              variant="outlined"
              placeholder={
                isBch ? 'Final Treatment Plan' : 'Final Treatment Plan*'
              }
              label={isBch ? 'Final Treatment Plan' : 'Final Treatment Plan*'}
              multiline
              rows={4}
              handleChangeFormData={(e) => {
                handleChangeFormData('finalTreatmentPlan', e.target.value)
              }}
              value={implantReportInputs.reportInputs['finalTreatmentPlan']}
              sx={{ marginTop: '10px' }}
            />
            {validationErrors?.finalTreatmentPlan?.[0] && (
              <Typography
                fontSize={12}
                color="var(--error)"
                ml={1}
                mt={-0.6}
                mb={0.5}
              >
                {validationErrors.finalTreatmentPlan[0]}
              </Typography>
            )}
            {isBch && (
              <TextInput
                variant="outlined"
                placeholder="Additional Comments"
                label="Additional Comments"
                handleChangeFormData={(e) => {
                  handleChangeFormData('additionalComments', e.target.value)
                }}
                value={implantReportInputs.reportInputs['additionalComments']}
              />
            )}
            <Box sx={{ width: '100%', textAlign: 'right', marginTop: '20px' }}>
              <CustomDatePicker
                sx={{ alignItems: 'flex-end' }}
                label="Procedure Date"
                className="scheduled-procedure-date-picker"
                value={
                  implantReportInputs.reportInputs[
                    'scheduledProcedureDate'
                  ] as dayjs.Dayjs
                }
                onDateSelection={(date) => {
                  handleChangeFormData('scheduledProcedureDate', date)
                }}
                minDate={dayjs(surgeryDate)}
                errorMessage={validationErrors?.scheduledProcedureDate?.[0]}
              />
            </Box>
          </Box>
        )}

        {implantReportInputs['reportType'] === 'SURGERY' && (
          <>
            <TextInput
              variant="outlined"
              placeholder="Procedure*"
              label="Procedure*"
              multiline
              rows={4}
              handleChangeFormData={(e) =>
                handleChangeFormData('procedure', e.target.value)
              }
              value={implantReportInputs.reportInputs.procedure || ''}
              errorMessage={validationErrors?.procedure?.[0]}
            />

            {isBch && (
              <TextInput
                variant="outlined"
                placeholder="Additional Comments"
                label="Additional Comments"
                handleChangeFormData={(e) => {
                  handleChangeFormData('additionalComments', e.target.value)
                }}
                value={implantReportInputs.reportInputs['additionalComments']}
              />
            )}
          </>
        )}

        {reportAlreadySentModal && (
          <AlertDialog
            isOpen={reportAlreadySentModal}
            mode="primary"
            title="Report Already Sent"
            description="This report has already been sent to the referring physician. Are you sure you want to send it again?"
            secondaryButtonAction={() => setReportAlreadySentModal(false)}
            primaryButtonAction={submitReport}
            primaryButtonText="Submit"
            isPrimaryLoading={sendImplantReportMutation.loading}
            zIndex={1300}
          />
        )}

        <div className="attachment-wrapper">
          {attachments.length > 0 ? (
            <div className="attachment-images-wrapper">
              {attachments.map(({ filename, thumbnail }, index) => (
                <div className="attachment-image-box">
                  <IconButton
                    className="delete-attachment"
                    onClick={handleRemoveAttachment(index)}
                  >
                    <Close className="close" />
                  </IconButton>
                  <img
                    src={thumbnail}
                    alt="thumbnail"
                    className="attachment-image"
                  />
                  <div className="titles ellipsis">{filename}</div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="titles" onClick={handleAttach}>
                Attach Documents (PDF, Image)
              </div>
            </>
          )}

          <IconButton disabled={attachments.length > 3} onClick={handleAttach}>
            <AttachFileIcon
              fontSize="small"
              sx={{
                color: `${
                  attachments.length < 4
                    ? 'var(--primary-dark)'
                    : 'var(--disabled)'
                }`,
              }}
            />
            <input
              hidden
              type="file"
              ref={fileInputRef}
              accept=".pdf, image/*"
              onChange={handleAttachementFileChange}
            />
          </IconButton>
        </div>

        {/* Custom Preview Button */}

        <LoadingButton
          variant="outlined"
          startIcon={<Visibility />}
          onClick={validateAndPreview}
          disabled={sendImplantReportMutation.loading}
          sx={{ mr: 2 }}
          fullWidth
          loading={previewImplantReportLoading}
        >
          Preview Report
        </LoadingButton>
      </CustomModal>

      {/* Preview Modal */}
      <ImplantReportPreviewModal
        open={previewModalOpen}
        onClose={handleClosePreview}
        pdfUrl={previewPdfUrl}
        reportType={formatEnumValue(
          implantReportInputs?.['reportType'] as keyof typeof ImplantReportType
        )}
        isLoading={previewImplantReportLoading}
      />
    </>
  )
}
